import { useMemo, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  Cage,
  CageStatus,
  CageType,
  CreateCageDto,
  FacingDirection,
  UpdateCageDto,
  CAGE_STATUS_LABELS,
  CAGE_TYPE_LABELS,
  FACING_LABELS,
  WIRE_TYPE_PRESETS,
  cubicVolumeCm3,
  formatVolume,
  type DimensionUnit,
  type NestBox,
} from '../api/cages';

interface CageFormProps {
  cage?: Cage;
  onSubmit: (data: CreateCageDto | UpdateCageDto) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const emptyForm = (): CreateCageDto => ({
  cageNumber: '',
  name: '',
  cageType: CageType.BREEDING,
  status: CageStatus.ACTIVE,
  wireType: '',
  facingDirection: undefined,
  location: '',
  dimensionUnit: 'cm',
  length: undefined,
  height: undefined,
  breadth: undefined,
  nestBoxes: [],
  floorType: '',
  hasCover: false,
  isOutdoor: false,
  notes: '',
});

function fromCage(cage: Cage): CreateCageDto {
  return {
    cageNumber: cage.cageNumber,
    name: cage.name || '',
    cageType: cage.cageType,
    status: cage.status,
    wireType: cage.wireType || '',
    facingDirection: cage.facingDirection || undefined,
    location: cage.location || '',
    dimensionUnit: cage.dimensionUnit || 'cm',
    length: cage.length != null ? Number(cage.length) : undefined,
    height: cage.height != null ? Number(cage.height) : undefined,
    breadth: cage.breadth != null ? Number(cage.breadth) : undefined,
    nestBoxes: cage.nestBoxes || [],
    floorType: cage.floorType || '',
    hasCover: cage.hasCover,
    isOutdoor: cage.isOutdoor,
    notes: cage.notes || '',
  };
}

export default function CageForm({ cage, onSubmit, onCancel, loading = false }: CageFormProps) {
  const [formData, setFormData] = useState<CreateCageDto>(cage ? fromCage(cage) : emptyForm());
  const [error, setError] = useState('');

  const volumeLabel = useMemo(
    () =>
      formatVolume(
        cubicVolumeCm3(
          formData.length || 0,
          formData.height || 0,
          formData.breadth || 0,
          formData.dimensionUnit || 'cm',
        ),
      ),
    [formData.length, formData.height, formData.breadth, formData.dimensionUnit],
  );

  const update = (patch: Partial<CreateCageDto>) => {
    setFormData((prev) => ({ ...prev, ...patch }));
  };

  const addNestBox = () => {
    const next: NestBox = {
      id: crypto.randomUUID(),
      label: `Box ${(formData.nestBoxes?.length || 0) + 1}`,
      type: '',
      position: '',
      notes: '',
    };
    update({ nestBoxes: [...(formData.nestBoxes || []), next] });
  };

  const updateNestBox = (id: string, patch: Partial<NestBox>) => {
    update({
      nestBoxes: (formData.nestBoxes || []).map((box) => (box.id === id ? { ...box, ...patch } : box)),
    });
  };

  const removeNestBox = (id: string) => {
    update({ nestBoxes: (formData.nestBoxes || []).filter((box) => box.id !== id) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.cageNumber.trim()) {
      setError('Cage number is required');
      return;
    }
    setError('');
    const payload: CreateCageDto = {
      ...formData,
      cageNumber: formData.cageNumber.trim(),
      name: formData.name?.trim() || undefined,
      wireType: formData.wireType?.trim() || undefined,
      location: formData.location?.trim() || undefined,
      floorType: formData.floorType?.trim() || undefined,
      notes: formData.notes?.trim() || undefined,
      nestBoxes: formData.nestBoxes || [],
    };
    await onSubmit(payload);
  };

  const field = 'mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cageNumber" className="block text-sm font-medium text-slate-700">
            Cage number <span className="text-red-500">*</span>
          </label>
          <input
            id="cageNumber"
            value={formData.cageNumber}
            onChange={(e) => update({ cageNumber: e.target.value })}
            className={field}
            required
            placeholder="e.g. A-12"
          />
        </div>
        <div>
          <label htmlFor="cageName" className="block text-sm font-medium text-slate-700">
            Name
          </label>
          <input
            id="cageName"
            value={formData.name || ''}
            onChange={(e) => update({ name: e.target.value })}
            className={field}
            placeholder="Optional display name"
          />
        </div>
        <div>
          <label htmlFor="cageType" className="block text-sm font-medium text-slate-700">
            Cage type
          </label>
          <select
            id="cageType"
            value={formData.cageType}
            onChange={(e) => update({ cageType: e.target.value as CageType })}
            className={field}
          >
            {Object.values(CageType).map((type) => (
              <option key={type} value={type}>
                {CAGE_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="cageStatus" className="block text-sm font-medium text-slate-700">
            Status
          </label>
          <select
            id="cageStatus"
            value={formData.status}
            onChange={(e) => update({ status: e.target.value as CageStatus })}
            className={field}
          >
            {Object.values(CageStatus).map((status) => (
              <option key={status} value={status}>
                {CAGE_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="location" className="block text-sm font-medium text-slate-700">
            Location
          </label>
          <input
            id="location"
            value={formData.location || ''}
            onChange={(e) => update({ location: e.target.value })}
            className={field}
            placeholder="Yard, room, or building"
          />
        </div>
        <div>
          <label htmlFor="facingDirection" className="block text-sm font-medium text-slate-700">
            Facing direction
          </label>
          <select
            id="facingDirection"
            value={formData.facingDirection || ''}
            onChange={(e) =>
              update({ facingDirection: (e.target.value || undefined) as FacingDirection | undefined })
            }
            className={field}
          >
            <option value="">Not set</option>
            {Object.values(FacingDirection).map((dir) => (
              <option key={dir} value={dir}>
                {FACING_LABELS[dir]}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="wireType" className="block text-sm font-medium text-slate-700">
            Wire type
          </label>
          <input
            id="wireType"
            list="wire-type-presets"
            value={formData.wireType || ''}
            onChange={(e) => update({ wireType: e.target.value })}
            className={field}
            placeholder="e.g. 13mm × 25mm welded mesh"
          />
          <datalist id="wire-type-presets">
            {WIRE_TYPE_PRESETS.map((preset) => (
              <option key={preset} value={preset} />
            ))}
          </datalist>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900">Dimensions</h3>
        <p className="mt-1 text-xs text-slate-500">
          Length × height × breadth. Cubic volume is calculated automatically.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-5">
          <div>
            <label htmlFor="dimensionUnit" className="block text-sm font-medium text-slate-700">
              Unit
            </label>
            <select
              id="dimensionUnit"
              value={formData.dimensionUnit}
              onChange={(e) => update({ dimensionUnit: e.target.value as DimensionUnit })}
              className={field}
            >
              <option value="mm">mm</option>
              <option value="cm">cm</option>
              <option value="m">m</option>
              <option value="inch">inch</option>
            </select>
          </div>
          <div>
            <label htmlFor="length" className="block text-sm font-medium text-slate-700">
              Length
            </label>
            <input
              id="length"
              type="number"
              min="0"
              step="0.01"
              value={formData.length ?? ''}
              onChange={(e) => update({ length: e.target.value === '' ? undefined : Number(e.target.value) })}
              className={field}
            />
          </div>
          <div>
            <label htmlFor="height" className="block text-sm font-medium text-slate-700">
              Height
            </label>
            <input
              id="height"
              type="number"
              min="0"
              step="0.01"
              value={formData.height ?? ''}
              onChange={(e) => update({ height: e.target.value === '' ? undefined : Number(e.target.value) })}
              className={field}
            />
          </div>
          <div>
            <label htmlFor="breadth" className="block text-sm font-medium text-slate-700">
              Breadth
            </label>
            <input
              id="breadth"
              type="number"
              min="0"
              step="0.01"
              value={formData.breadth ?? ''}
              onChange={(e) => update({ breadth: e.target.value === '' ? undefined : Number(e.target.value) })}
              className={field}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Volume</label>
            <p className="mt-2 text-sm font-semibold text-slate-900">{volumeLabel}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor="floorType" className="block text-sm font-medium text-slate-700">
            Floor type
          </label>
          <input
            id="floorType"
            value={formData.floorType || ''}
            onChange={(e) => update({ floorType: e.target.value })}
            className={field}
            placeholder="Wire, concrete, sand…"
          />
        </div>
        <label className="mt-6 flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={!!formData.hasCover}
            onChange={(e) => update({ hasCover: e.target.checked })}
            className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
          />
          Has cover / roof
        </label>
        <label className="mt-6 flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={!!formData.isOutdoor}
            onChange={(e) => update({ isOutdoor: e.target.checked })}
            className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
          />
          Outdoor
        </label>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Nest boxes</h3>
          <button type="button" onClick={addNestBox} className="inline-flex items-center gap-1 text-sm text-cyan-700 hover:underline">
            <PlusIcon className="h-4 w-4" />
            Add nest box
          </button>
        </div>
        {(formData.nestBoxes || []).length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">No nest boxes on this cage yet.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {(formData.nestBoxes || []).map((box) => (
              <div key={box.id} className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 p-3 sm:grid-cols-12">
                <input
                  value={box.label}
                  onChange={(e) => updateNestBox(box.id, { label: e.target.value })}
                  className={`${field} sm:col-span-3`}
                  placeholder="Label"
                />
                <input
                  value={box.type || ''}
                  onChange={(e) => updateNestBox(box.id, { type: e.target.value })}
                  className={`${field} sm:col-span-3`}
                  placeholder="Type (horizontal, grandfather…)"
                />
                <input
                  value={box.position || ''}
                  onChange={(e) => updateNestBox(box.id, { position: e.target.value })}
                  className={`${field} sm:col-span-3`}
                  placeholder="Position"
                />
                <div className="flex items-center gap-2 sm:col-span-3">
                  <input
                    value={box.notes || ''}
                    onChange={(e) => updateNestBox(box.id, { notes: e.target.value })}
                    className={field}
                    placeholder="Notes"
                  />
                  <button
                    type="button"
                    onClick={() => removeNestBox(box.id)}
                    className="p-2 text-slate-400 hover:text-red-600"
                    aria-label="Remove nest box"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <label htmlFor="cageNotes" className="block text-sm font-medium text-slate-700">
          Notes
        </label>
        <textarea
          id="cageNotes"
          rows={3}
          value={formData.notes || ''}
          onChange={(e) => update({ notes: e.target.value })}
          className={field}
        />
      </div>

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={loading}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving…' : cage ? 'Save cage' : 'Add cage'}
        </button>
      </div>
    </form>
  );
}
